/**
 * PesacoreArchitect.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Moteur IA PESACORE — Architecte de situations sociales complexes
 *
 * Principe fondateur :
 *   Chaque persona est une équation sociale. L'IA ne génère pas du texte —
 *   elle résout des structures : droits activables, financements mobilisables,
 *   dossiers constituables, trajectoires optimisables.
 *
 * Ce que l'IA produit (outputs structurés) :
 *   1. DIAGNOSTIC    — lecture de la situation réelle (persona + données saisies)
 *   2. ÉQUATION      — droits × contraintes × leviers disponibles
 *   3. PLAN D'ACTION — séquence ordonnée d'actions concrètes
 *   4. DOSSIERS      — structures pré-remplies (financement, certification, CRM)
 *   5. TRAJECTOIRE   — projection à 3/6/12 mois selon le profil
 *
 * Architecture :
 *   - SystemPrompt injecté dynamiquement depuis le profil + persona actif
 *   - Historique de conversation maintenu en mémoire (multi-tour)
 *   - Outputs parsés en JSON → composants UI actionnables
 *   - Chaque output peut déclencher une action dans le dashboard
 *     (pré-remplir un dossier, créer un contact CRM, générer un PDF...)
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Brain,
  Check,
  ChevronRight,
  Clock,
  Copy,
  Cpu,
  Euro,
  FileCheck,
  Layers,
  RotateCw,
  Send,
  Shield,
  Sparkles,
  Target,
  TrendingUp, Users,
  Zap
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getProfile } from '../../config/activityProfileConfig2';
import api from '../../config/axiosConfig';
import useGrandeEcole from '../../stores/useGrandeEcole';

// ─────────────────────────────────────────────────────────────────────────────
// SYSTÈME DE PROMPTS — le cerveau de l'équation sociale
// ─────────────────────────────────────────────────────────────────────────────

async function callPesacore({ messages, profileId, personaPath, formData, sessionId }) {
  const { data } = await api.post('/admin/pesacore/architect', {
    messages,
    profileId,
    personaPath,
    formData,
    sessionId: sessionId ?? undefined,
  });
  return data.output; // { type, titre, synthese, blocs[], prochaine_etape }
}
 
async function resetSession(sessionId) {
  if (!sessionId) return;
  await api.delete(`/admin/pesacore/history/${sessionId}`).catch(() => {});
}

// ─────────────────────────────────────────────────────────────────────────────
// PROMPTS DE DÉMARRAGE — selon le persona détecté
// ─────────────────────────────────────────────────────────────────────────────

function getStarterPrompts(profile, personaPath) {
  const leaf = personaPath[personaPath.length - 1] ?? '';
  const primary = profile?.certificationFramework?.primary ?? 'qualiopi';

  const base = [
    { icon: FileCheck, label: 'Diagnostic certification', prompt: `Fais un diagnostic complet de ma situation ${primary.toUpperCase()} et dis-moi ce qui manque pour être en conformité.` },
    { icon: Euro,      label: 'Plan de financement',      prompt: `Analyse mes données et construis le plan de financement optimal que je peux activer immédiatement. Priorise par montant et facilité d'obtention.` },
    { icon: BookOpen,  label: 'Dossier pré-rempli',       prompt: `Génère la structure d'un dossier de financement pré-rempli avec mes données actuelles. Identifie ce qui manque.` },
    { icon: TrendingUp,label: 'Trajectoire 12 mois',      prompt: `Projette ma trajectoire sur 12 mois : quelles certifications obtenir, quels financements activer, dans quel ordre pour maximiser la valeur.` },
  ];

  // Starters spécifiques selon le persona leaf
  const specific = {
    'cfa': [
      { icon: Users, label: 'Optimisation alternance', prompt: `Analyse mon profil CFA et dis-moi comment optimiser le taux de rupture de contrat et maximiser le financement OPCO par apprenti.` },
    ],
    'organisme-prive': [
      { icon: Shield, label: 'Audit Qualiopi complet', prompt: `Fais un audit Qualiopi des 7 critères et donne-moi la liste exacte des preuves à produire pour l'audit de surveillance.` },
    ],
    'pme': [
      { icon: Target, label: 'Plan de compétences PDC', prompt: `Construis la structure d'un Plan de Développement des Compétences optimisé pour mon OPCO de rattachement.` },
    ],
    'grand-compte': [
      { icon: Layers, label: 'Stratégie formation groupe', prompt: `Analyse ma situation grand compte et propose une architecture de formation qui maximise les remontées OPCO et minimise le reste à charge.` },
    ],
    'andpc': [
      { icon: Clock, label: 'Parcours DPC obligatoire', prompt: `Quelles sont mes obligations DPC triennales et comment les satisfaire avec les modules disponibles sur la plateforme ?` },
    ],
  };

  return [...base, ...(specific[leaf] ?? [])].slice(0, 5);
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANTS UI — rendu des outputs structurés
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIE_STYLES = {
  financement   : { color: '#22d3ee',  bg: 'rgba(34,211,238,0.06)',  border: 'rgba(34,211,238,0.2)',  icon: Euro      },
  certification : { color: '#f59e0b',  bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.2)',  icon: FileCheck  },
  action        : { color: '#a78bfa',  bg: 'rgba(167,139,250,0.06)',border: 'rgba(167,139,250,0.2)', icon: Zap        },
  alerte        : { color: '#f87171',  bg: 'rgba(248,113,113,0.06)', border: 'rgba(248,113,113,0.2)', icon: AlertTriangle },
  opportunite   : { color: '#34d399',  bg: 'rgba(52,211,153,0.06)', border: 'rgba(52,211,153,0.2)',  icon: Sparkles   },
  document      : { color: '#94a3b8',  bg: 'rgba(148,163,184,0.04)',border: 'rgba(148,163,184,0.15)',icon: BookOpen   },
  trajectoire   : { color: '#fb923c',  bg: 'rgba(251,146,60,0.06)', border: 'rgba(251,146,60,0.2)',  icon: TrendingUp },
};

const PRIORITE_LABELS = { 1: 'CRITIQUE', 2: 'URGENT', 3: 'IMPORTANT', 4: 'UTILE', 5: 'OPTIONNEL' };
const PRIORITE_COLORS = { 1: '#f87171', 2: '#fb923c', 3: '#f59e0b',   4: '#22d3ee', 5: '#52525b'  };

function BlocCard({ bloc, onAction, delay = 0 }) {
  const style  = CATEGORIE_STYLES[bloc.categorie] ?? CATEGORIE_STYLES.action;
  const Icon   = style.icon;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = [bloc.titre, bloc.contenu, ...(bloc.items ?? [])].join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.25 }}
      className="relative overflow-hidden"
      style={{ background: style.bg, border: `1px solid ${style.border}` }}
    >
      {/* Barre de priorité */}
      <div
        className="absolute top-0 left-0 w-0.5 h-full"
        style={{ background: PRIORITE_COLORS[bloc.priorite] ?? '#52525b' }}
      />

      <div className="px-4 py-3 pl-5">
        {/* En-tête du bloc */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon size={12} style={{ color: style.color, flexShrink: 0 }} />
            <span className="text-[10px] font-mono font-black uppercase tracking-widest truncate" style={{ color: style.color }}>
              {bloc.titre}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className="text-[8px] font-mono px-1.5 py-0.5"
              style={{ color: PRIORITE_COLORS[bloc.priorite], border: `1px solid ${PRIORITE_COLORS[bloc.priorite]}30`, background: `${PRIORITE_COLORS[bloc.priorite]}10` }}
            >
              {PRIORITE_LABELS[bloc.priorite] ?? 'P' + bloc.priorite}
            </span>
            <button onClick={handleCopy} className="opacity-40 hover:opacity-100 transition-opacity">
              {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} className="text-zinc-400" />}
            </button>
          </div>
        </div>

        {/* Contenu */}
        {bloc.contenu && (
          <p className="text-[11px] font-mono text-zinc-300 leading-relaxed mb-2">{bloc.contenu}</p>
        )}

        {/* Items */}
        {bloc.items?.length > 0 && (
          <ul className="space-y-1 mb-3">
            {bloc.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-[10px] font-mono text-zinc-400">
                <ChevronRight size={10} className="flex-shrink-0 mt-0.5" style={{ color: style.color }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Bouton d'action */}
        {bloc.actionnable && bloc.action && (
          <button
            onClick={() => onAction?.(bloc.action)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono font-black uppercase tracking-wider transition-all"
            style={{ background: `${style.color}15`, border: `1px solid ${style.color}40`, color: style.color }}
          >
            <ArrowRight size={10} />
            {bloc.action.label}
          </button>
        )}
      </div>
    </motion.div>
  );
}

function OutputCard({ output, onAction }) {
  if (!output?.blocs) return null;

  const TYPE_ICONS = {
    diagnostic  : Brain,
    equation    : Cpu,
    plan        : Target,
    dossier     : FileCheck,
    trajectoire : TrendingUp,
    reponse     : Sparkles,
  };
  const TypeIcon = TYPE_ICONS[output.type] ?? Sparkles;

  // Tri par priorité
  const sorted = [...(output.blocs)].sort((a, b) => (a.priorite ?? 5) - (b.priorite ?? 5));

  return (
    <div className="space-y-3">
      {/* En-tête output */}
      <div className="flex items-center gap-2 px-1">
        <TypeIcon size={12} className="text-amber-500/60 flex-shrink-0" />
        <span className="text-[9px] font-mono uppercase tracking-widest text-amber-500/50">{output.type}</span>
        <span className="text-[10px] font-mono text-zinc-300 font-black ml-1">{output.titre}</span>
      </div>

      {/* Synthèse */}
      {output.synthese && (
        <div
          className="px-4 py-2.5 text-[11px] font-mono text-zinc-300 italic leading-relaxed"
          style={{ background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.1)', borderLeft: '2px solid rgba(245,158,11,0.4)' }}
        >
          {output.synthese}
        </div>
      )}

      {/* Blocs */}
      <div className="space-y-2">
        {sorted.map((bloc, i) => (
          <BlocCard key={bloc.id ?? i} bloc={bloc} onAction={onAction} delay={i * 0.05} />
        ))}
      </div>

      {/* Prochaine étape */}
      {output.prochaine_etape && (
        <div
          className="flex items-start gap-2 px-3 py-2.5 text-[10px] font-mono text-zinc-400"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
        >
          <Cpu size={9} className="text-cyan-500/40 mt-0.5 flex-shrink-0" />
          <span>{output.prochaine_etape}</span>
        </div>
      )}
    </div>
  );
}

// Message utilisateur simple
function UserMessage({ content }) {
  return (
    <div className="flex justify-end">
      <div
        className="max-w-[80%] px-4 py-2.5 text-[11px] font-mono text-zinc-200"
        style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)' }}
      >
        {content}
      </div>
    </div>
  );
}

// Indicateur de chargement
function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-3 px-4 py-3" style={{ background: 'rgba(34,211,238,0.03)', border: '1px solid rgba(34,211,238,0.08)' }}>
      <Brain size={13} className="text-cyan-500/50 animate-pulse" />
      <div className="flex gap-1">
        {[0, 1, 2].map(i => (
          <motion.div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-cyan-500/40"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
          />
        ))}
      </div>
      <span className="text-[9px] font-mono uppercase tracking-widest text-cyan-500/30">
        Résolution en cours…
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

export default function PesacoreArchitect({
  profileId,
  personaPath = [],
  formData = {},
  tenantOnboardingData = {},
  sessionId = null,
  onAction,
  className = '',
}) {
  const profile       = getProfile(profileId);  
  const starters      = getStarterPrompts(profile, personaPath);

  const activityProfile = useGrandeEcole(state => state.activityProfile);

  // Historique des messages
  const [messages,    setMessages]    = useState([]);   // { role, content, output? }
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [initialized, setInitialized] = useState(false);

  const scrollRef = useRef(null);
  const inputRef  = useRef(null);

  // Scroll automatique
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Message d'amorce automatique quand le persona est défini
  useEffect(() => {
    if (personaPath.length > 0 && !initialized && messages.length === 0) {
      setInitialized(true);
      sendMessage(`Analyse ma situation et fais un premier diagnostic. Profil : ${personaPath.join(' › ')}.`, true);
    }
  }, [personaPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = useCallback(async (text, silent = false) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text.trim() };

    setMessages(prev => silent ? prev : [...prev, { ...userMsg, silent: false }]);
    setInput('');
    setLoading(true);
    setError(null);

    const history = [
      ...(silent ? [] : messages.map(m => ({ role: m.role, content: m.content }))),
      userMsg,
    ];

    try {
      
      const output = await callPesacore({
        messages: history,
        profileId: activityProfile,
        personaPath,
        formData,
        sessionId: sessionId ?? undefined
      });

      const assistantMsg = {
        role: 'assistant',
        content: JSON.stringify(output),
        output,
        silent: false,
      };

      setMessages(prev => [
        ...(silent ? [] : prev),
        ...(silent ? [{ ...userMsg, silent: true }] : []),
        assistantMsg,
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [
    messages, 
    loading, 
    activityProfile, 
    personaPath,     
    formData,        
    sessionId        
  ]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    sendMessage(input);
  };

  const handleStarter = (starter) => sendMessage(starter.prompt);

  const handleAction = useCallback((action) => {
    onAction?.(action);
  }, [onAction]);

  const handleReset = () => {
    setMessages([]);
    setInitialized(false);
    setError(null);
  };

  const hasPersona = personaPath.length > 0;

  return (
    <div
      className={`flex flex-col ${className}`}
      style={{
        background : 'rgba(2,6,23,0.95)',
        border     : '1px solid rgba(34,211,238,0.08)',
        minHeight  : 520,
        maxHeight  : 700,
      }}
    >
      {/* ── En-tête ────────────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-5 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(34,211,238,0.06)' }}
      >
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <Brain size={16} className="text-cyan-400/70" />
            <motion.div
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
          <div>
            <span className="text-[11px] font-mono font-black uppercase tracking-widest text-cyan-400/80">
              PESACORE Architect
            </span>
            {hasPersona && (
              <div className="text-[9px] font-mono text-zinc-600 mt-0.5">
                {personaPath.join(' › ')}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {messages.length > 0 && (
            <button
              onClick={handleReset}
              className="text-zinc-600 hover:text-zinc-400 transition-colors"
              title="Réinitialiser"
            >
              <RotateCw size={13} />
            </button>
          )}
          <div
            className="flex items-center gap-1.5 px-2 py-1"
            style={{ background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.12)' }}
          >
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
            <span className="text-[8px] font-mono uppercase tracking-widest text-cyan-500/60">
              {hasPersona ? 'Actif' : 'En attente de persona'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Corps scrollable ───────────────────────────────────────────────── */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* Starters — affichés seulement si pas encore de messages et persona défini */}
        {messages.filter(m => !m.silent).length === 0 && !loading && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              {/* Prompt d'état si pas de persona */}
              {!hasPersona && (
                <div
                  className="px-4 py-6 text-center"
                  style={{ background: 'rgba(245,158,11,0.03)', border: '1px solid rgba(245,158,11,0.08)' }}
                >
                  <Brain size={24} className="mx-auto mb-3 text-amber-500/30" />
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-1">
                    En attente de configuration
                  </div>
                  <div className="text-xs font-mono text-zinc-500">
                    Sélectionnez votre persona dans le formulaire<br />
                    pour activer l'analyse PESACORE
                  </div>
                </div>
              )}

              {/* Starters */}
              {hasPersona && (
                <>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-700 px-1">
                    Analyses disponibles
                  </div>
                  <div className="space-y-2">
                    {starters.map((s, i) => (
                      <motion.button
                        key={i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.08 }}
                        onClick={() => handleStarter(s)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left group transition-all"
                        style={{
                          background : 'rgba(255,255,255,0.02)',
                          border     : '1px solid rgba(255,255,255,0.05)',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,158,11,0.2)'; e.currentTarget.style.background = 'rgba(245,158,11,0.04)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                      >
                        <s.icon size={13} className="text-amber-500/50 group-hover:text-amber-400 transition-colors flex-shrink-0" />
                        <span className="text-[11px] font-mono text-zinc-400 group-hover:text-zinc-200 transition-colors">
                          {s.label}
                        </span>
                        <ArrowRight size={11} className="ml-auto text-zinc-700 group-hover:text-amber-500/50 transition-colors flex-shrink-0" />
                      </motion.button>
                    ))}
                  </div>
                </>
              )}

              {/* Zone de saisie libre */}
              {hasPersona && (
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-700 px-1 pt-2">
                  Ou posez votre question
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}

        {/* Messages */}
        {messages.filter(m => !m.silent).map((msg, i) => (
          <div key={i}>
            {msg.role === 'user' && !msg.silent && (
              <UserMessage content={msg.content} />
            )}
            {msg.role === 'assistant' && msg.output && (
              <OutputCard output={msg.output} onAction={handleAction} />
            )}
          </div>
        ))}

        {/* Chargement */}
        {loading && <ThinkingIndicator />}

        {/* Erreur */}
        {error && (
          <div
            className="flex items-center gap-2 px-4 py-3 text-[10px] font-mono text-red-400"
            style={{ background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.2)' }}
          >
            <AlertTriangle size={11} className="flex-shrink-0" />
            <span>Erreur : {error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-zinc-600 hover:text-zinc-400">
              ✕
            </button>
          </div>
        )}
      </div>

      {/* ── Zone de saisie ─────────────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 p-3"
        style={{ borderTop: '1px solid rgba(34,211,238,0.06)' }}
      >
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
            placeholder={
              hasPersona
                ? 'Posez votre question ou décrivez votre situation…'
                : 'Configurez d\'abord votre persona…'
            }
            disabled={loading || !hasPersona}
            className="flex-1 bg-black/40 px-3 py-2.5 text-[11px] font-mono text-white outline-none transition-all disabled:opacity-40 placeholder:text-zinc-700"
            style={{
              border     : '1px solid rgba(34,211,238,0.1)',
              caretColor : '#f59e0b',
            }}
            onFocus={e  => { if (hasPersona) e.target.style.borderColor = 'rgba(34,211,238,0.3)'; }}
            onBlur={e   => { e.target.style.borderColor = 'rgba(34,211,238,0.1)'; }}
          />
          <button
            onClick={handleSubmit}
            disabled={loading || !input.trim() || !hasPersona}
            className="px-3 py-2.5 flex items-center gap-1.5 text-[10px] font-mono font-black uppercase tracking-wider transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background : 'rgba(245,158,11,0.1)',
              border     : '1px solid rgba(245,158,11,0.3)',
              color      : '#f59e0b',
            }}
          >
            <Send size={12} />
          </button>
        </div>

        {/* Hint */}
        <div className="flex items-center justify-between mt-2 px-0.5">
          <span className="text-[8px] font-mono text-zinc-700 uppercase tracking-widest">
            PESACORE Architect v1 · Modèle souverain · FR
          </span>
          {messages.filter(m => !m.silent && m.role === 'assistant').length > 0 && (
            <button
              className="text-[8px] font-mono text-zinc-700 hover:text-zinc-500 transition-colors uppercase tracking-widest"
              onClick={() => {
                const allText = messages
                  .filter(m => m.output)
                  .map(m => [m.output.titre, m.output.synthese, ...(m.output.blocs ?? []).flatMap(b => [b.titre, b.contenu, ...(b.items ?? [])])].join('\n'))
                  .join('\n\n---\n\n');
                navigator.clipboard.writeText(allText);
              }}
            >
              <Copy size={9} className="inline mr-1" />
              Copier tout
            </button>
          )}
        </div>
      </div>
    </div>
  );
}